/* =====================================================================================
   File upload.

   Two steps that must both succeed: the bytes go to Supabase Storage, then a row in
   app.attachments records what the file IS and what it belongs to. A storage bucket has no
   idea that a PDF is version 3 of SOP-012 awaiting the Quality Manager.

   THE PATH IS THE TENANT BOUNDARY:

       {org_id}/{object_type}/{object_id}/{timestamp}-{filename}

   Storage policies in migration 0017 check that first segment against the caller's
   organisation, so a member of one tenant cannot reach another's files even knowing the
   exact path. Get the path wrong and the upload is refused, which is the right failure.

   Buckets are private. Downloads therefore use short-lived signed URLs rather than public
   links, because a public link to a signed contract or an unapproved marketing claim is one
   forwarded email away from being everyone's.
   ===================================================================================== */

import { BACKEND } from './db';

/* Must match the attachments_bucket_check constraint in migration 0040. A bucket present in
   storage but absent from that constraint uploads the file and then fails to record it, which
   is what happened to `technical`; the migration now refuses to apply if the two disagree. */
export type Bucket = 'technical' | 'circus' | 'assets' | 'grants' | 'quality'
                   | 'contracts' | 'general';
export type AttachmentRole = 'attachment' | 'draft' | 'submitted' | 'signed' | 'rendition' | 'evidence';

export interface Attachment {
  id: string;
  object_type: string;
  object_id: string;
  bucket: Bucket;
  path: string;
  filename: string;
  mime_type: string | null;
  bytes: number | null;
  role: AttachmentRole;
  /** One plain sentence saying what the file is. A filename is not a description:
      "final_v3 (2).pdf" recognises nothing, and the duplicate check depends on a human
      being able to see that a document is already filed. Null on anything uploaded
      before migration 0093. */
  description: string | null;
  uploaded_by: string;
  uploaded_by_name?: string;
  uploaded_at: string;
}

/* Per-bucket ceilings, matching the limits set on the buckets themselves. Checked here too
   so a 200 MB file fails instantly with a sentence a person can act on, rather than after a
   two-minute upload and an opaque server error. */
const LIMITS: Record<Bucket, number> = {
  // Vendor decks and validation reports run large; a 60-slide PowerPoint with embedded
  // instrument screenshots routinely passes 40 MB.
  technical: 75 * 1024 * 1024,
  /* Competitor material arrives as investor decks, congress panel photographs and scanned
     brochures, which are large and image-heavy. Matches the 50 MB set on the bucket in 0040. */
  circus: 50 * 1024 * 1024,
  assets: 100 * 1024 * 1024,
  grants: 50 * 1024 * 1024,
  quality: 50 * 1024 * 1024,
  contracts: 50 * 1024 * 1024,
  general: 25 * 1024 * 1024,
};

export function humanSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/* Storage keys tolerate far less than a filename does. Spaces, accents and slashes all
   cause trouble, and a slash would silently create a folder, which for us means escaping
   the tenant prefix. The original name is preserved in the registry row. */
function safeName(name: string): string {
  return name
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 120) || 'file';
}

async function sha256(file: File): Promise<string | null> {
  try {
    const buf = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;   // a checksum is useful, not essential; never block an upload for it
  }
}

async function orgId(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  if (!data?.id) throw new Error('No organisation visible to this member.');
  return data.id;
}

export async function fetchAttachments(objectType: string, objectId: string): Promise<Attachment[]> {
  if (BACKEND !== 'supabase') return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('app').from('attachments')
    .select('*')
    .eq('object_type', objectType).eq('object_id', objectId)
    .is('removed_at', null)
    .order('uploaded_at', { ascending: false });
  if (error) throw new Error(error.message);
  const name = await (await import('./supabase')).nameOf();
  return (data ?? []).map((a: any) => ({ ...a, uploaded_by_name: name(a.uploaded_by) }));
}

/**
 * How many live attachments each of these objects carries, in one query. The Circus
 * summary cards show the count so a reader can see at a glance whether a competitor's
 * file is thin or thick without opening it. RLS scopes the rows to the caller's
 * organisation exactly as the per-object fetch is.
 */
export async function fetchAttachmentCounts(
  objectType: string, objectIds: string[],
): Promise<Record<string, number>> {
  if (BACKEND !== 'supabase' || objectIds.length === 0) return {};
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('app').from('attachments')
    .select('object_id')
    .eq('object_type', objectType).in('object_id', objectIds)
    .is('removed_at', null);
  if (error) throw new Error(error.message);
  const counts: Record<string, number> = {};
  for (const row of (data ?? []) as { object_id: string }[]) {
    counts[row.object_id] = (counts[row.object_id] ?? 0) + 1;
  }
  return counts;
}

export async function uploadFile(opts: {
  file: File;
  bucket: Bucket;
  objectType: string;
  objectId: string;
  role?: AttachmentRole;
  /** Optional everywhere except by convention on screens that list existing documents
      first: there the description is the thing the next person checks against. */
  description?: string | null;
}): Promise<Attachment> {
  const { file, bucket, objectType, objectId, role = 'attachment' } = opts;
  const description = opts.description?.trim() ? opts.description.trim().slice(0, 500) : null;

  if (file.size > LIMITS[bucket]) {
    throw new Error(
      `${file.name} is ${humanSize(file.size)}. The limit for this area is ${humanSize(LIMITS[bucket])}.`,
    );
  }

  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  if (!me?.id) throw new Error('No member record for the signed-in user.');
  const org = await orgId();

  // Timestamp prefix so re-uploading the same filename versions rather than overwrites. The
  // registry unique key is (bucket, path), so a collision would otherwise be a hard error at
  // exactly the wrong moment.
  const path = `${org}/${objectType}/${objectId}/${Date.now()}-${safeName(file.name)}`;

  const { error: upErr } = await supabase().storage.from(bucket).upload(path, file, {
    cacheControl: '3600', upsert: false, contentType: file.type || undefined,
  });
  if (upErr) {
    if (/row-level security|not authorized|Unauthorized/i.test(upErr.message)) {
      throw new Error('Refused by storage policy. Your account cannot write to this area.');
    }
    throw new Error(upErr.message);
  }

  const { data, error } = await supabase().schema('app').from('attachments').insert({
    org_id: org, object_type: objectType, object_id: objectId,
    bucket, path, filename: file.name,
    mime_type: file.type || null, bytes: file.size,
    checksum: await sha256(file), role, description, uploaded_by: me.id,
  }).select('*').single();

  if (error) {
    // The bytes landed but the record did not, which would leave an orphan nobody can find.
    // Remove the object so the two cannot disagree.
    await supabase().storage.from(bucket).remove([path]).catch(() => {});
    throw new Error(`Uploaded, but could not be recorded: ${error.message}`);
  }
  return data as Attachment;
}

/** Short-lived link for a private object. Sixty seconds is enough to start a download. */
export async function signedUrl(bucket: Bucket, path: string, seconds = 60): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().storage.from(bucket).createSignedUrl(path, seconds);
  if (error) throw new Error(error.message);
  return data.signedUrl;
}

/**
 * Withdraw rather than delete. A file referenced by an approved controlled document must
 * stay retrievable even after someone tidies up, so the object itself is left in place and
 * the registry records who removed it and when.
 */
export async function removeAttachment(id: string) {
  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  const { error } = await supabase().schema('app').from('attachments')
    .update({ removed_at: new Date().toISOString(), removed_by: me?.id })
    .eq('id', id);
  if (error) throw new Error(error.message);
}
